import { forwardRef } from 'react';
import { cn } from '../cn.js';

/**
 * A card: a bounded group of related things.
 *
 * Deliberately plain. A card is a container, and a container that draws attention is competing
 * with what it contains — so this is a surface, a border and a radius, and no shadow by default.
 * Elevation is for things that float above the page, which a card does not.
 */
export const Card = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function Card(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-(--radius-md) border border-(--color-border) bg-(--color-surface)',
        className,
      )}
      {...props}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-(--space-xs) p-(--space-md)', className)}
        {...props}
      />
    );
  },
);

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** The heading level, so a card inside a section does not break the document outline. */
  as?: 'h2' | 'h3' | 'h4';
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(function CardTitle(
  { className, as: Heading = 'h3', ...props },
  ref,
) {
  return (
    <Heading
      ref={ref}
      className={cn(
        'text-(length:--font-size-body-large) font-semibold text-(--color-text-primary)',
        className,
      )}
      {...props}
    />
  );
});

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return (
    <p
      ref={ref}
      className={cn('text-(length:--font-size-caption) text-(--color-text-secondary)', className)}
      {...props}
    />
  );
});

export const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cn('p-(--space-md) pt-0', className)} {...props} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-(--space-sm) border-t border-(--color-border) p-(--space-md)',
          className,
        )}
        {...props}
      />
    );
  },
);
